import { api } from './api.ts';
import {
  loadEmbeddings,
  loadFields,
  loadText,
  type OpenedDocument,
  type StructuredFields,
} from './documents.ts';

/**
 * Universelle KI-Anfrage (Konzept 3.11 / 6.7).
 *
 * Die Auswahl der relevanten Dokumente passiert hier im Browser. An den
 * Server – und damit an das Sprachmodell – geht ausschließlich die bereits
 * eingegrenzte Teilmenge, niemals der ganze Akten-Safe.
 */

export interface PreparedDocument {
  document: OpenedDocument;
  text: string;
  fields: StructuredFields | null;
  /**
   * Ein Vektor je Textabschnitt (crypto/primitives.ts, encryptVectors).
   * `null`, wenn das Dokument nie eingebettet wurde – solche Dokumente
   * müssen trotzdem auffindbar bleiben, siehe askQuestion.
   */
  embeddings: Float32Array[] | null;
}

export interface AnswerResult {
  answer: string;
  sources: Array<{ documentId: string; filename: string }>;
  /** Aus Strukturfeldern berechnet, nicht vom Sprachmodell geschätzt. */
  computed: ComputedValues | null;
}

export interface ComputedValues {
  documentCount: number;
  sumAmount?: number;
  currency?: string;
  earliestDate?: string;
  latestDate?: string;
  senders?: string[];
}

/**
 * Lädt und entschlüsselt die Sidecars der zugänglichen Dokumente. Ergebnis
 * bleibt im Arbeitsspeicher der Sitzung.
 */
export async function prepareDocuments(
  documents: OpenedDocument[],
  onProgress?: (done: number, total: number) => void,
): Promise<PreparedDocument[]> {
  const prepared: PreparedDocument[] = [];

  for (const [index, document] of documents.entries()) {
    const [text, fields, embeddings] = await Promise.all([
      loadText(document),
      loadFields(document),
      loadEmbeddings(document),
    ]);
    prepared.push({ document, text: text ?? '', fields, embeddings });
    onProgress?.(index + 1, documents.length);
  }

  return prepared;
}

/**
 * Cosinus-Ähnlichkeit. Der Wert liegt zwischen -1 und 1; bei Embeddings
 * bedeutet nahe 1 "inhaltlich ähnlich".
 */
export function cosineSimilarity(a: ArrayLike<number>, b: ArrayLike<number>): number {
  if (a.length !== b.length) return 0;
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i += 1) {
    dot += a[i]! * b[i]!;
    normA += a[i]! * a[i]!;
    normB += b[i]! * b[i]!;
  }
  const denominator = Math.sqrt(normA) * Math.sqrt(normB);
  return denominator === 0 ? 0 : dot / denominator;
}

/**
 * Unterhalb dieser Ähnlichkeit gilt ein Dokument als nicht passend. Lieber
 * "Dazu liegen mir keine passenden Unterlagen vor" als eine Antwort aus
 * Dokumenten, die zufällig entfernt ähnlich klingen (Konzept 3.11 Punkt 8).
 */
const RELEVANCE_THRESHOLD = 0.25;
const MAX_DOCUMENTS_PER_QUERY = 8;

/**
 * Berechnet Zahlen und Zeiträume aus den Strukturfeldern – ausdrücklich
 * nicht vom Sprachmodell (Konzept 3.11 Punkt 6). Das Modell formuliert nur.
 */
export function computeFromFields(documents: PreparedDocument[]): ComputedValues | null {
  const fields = documents
    .map((entry) => entry.fields)
    .filter((value): value is StructuredFields => value !== null);
  if (fields.length === 0) return null;

  const amounts = fields
    .map((field) => field.amount)
    .filter((value): value is number => typeof value === 'number');
  const dates = fields
    .map((field) => field.date)
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .sort();
  const senders = [
    ...new Set(
      fields
        .map((field) => field.sender)
        .filter((value): value is string => typeof value === 'string' && value !== ''),
    ),
  ];

  const computed: ComputedValues = { documentCount: documents.length };

  if (amounts.length > 0) {
    // Rundung auf Cent: Gleitkommaaddition liefert sonst Werte wie 41.870000000000005.
    computed.sumAmount = Math.round(amounts.reduce((sum, value) => sum + value, 0) * 100) / 100;
    const currency = fields.find((field) => field.currency)?.currency;
    if (currency) computed.currency = currency;
  }
  if (dates.length > 0) {
    computed.earliestDate = dates[0];
    computed.latestDate = dates[dates.length - 1];
  }
  if (senders.length > 0) computed.senders = senders;

  return computed;
}

/**
 * Stellt eine Frage an den zugänglichen Akten-Safe.
 *
 * Ablauf entsprechend Konzept 3.11: Frage einbetten → lokal die passenden
 * Dokumente bestimmen → nur diese an das Sprachmodell → Antwort mit
 * Quellenangabe.
 */
export async function askQuestion(
  question: string,
  documents: PreparedDocument[],
): Promise<AnswerResult> {
  // Ein Dokument ist per Vektor vergleichbar, wenn es Embeddings hat UND
  // diese vom selben Modell stammen wie die gleich erzeugte Frage.
  const questionModel = await (async () => {
    if (!documents.some((entry) => entry.embeddings?.length)) return null;
    const { embedding, model } = await api.post<{ embedding: number[]; model: string }>(
      '/api/ai/embed',
      { input: question, inputType: 'query' },
    );
    return { vector: embedding, model };
  })();

  const vergleichbar = questionModel
    ? documents.filter(
        (entry) =>
          entry.embeddings?.length &&
          entry.document.meta.embedding_model === questionModel.model,
      )
    : [];

  // Vektortreffer: Ähnlichkeit eines Dokuments ist die seines BESTEN
  // Abschnitts. Ein Vertrag, dessen Kündigungsfrist auf Seite 8 steht, soll
  // gefunden werden, auch wenn die übrigen Seiten nichts damit zu tun haben.
  const perVektor = vergleichbar
    .map((entry) => ({
      entry,
      score: Math.max(
        ...entry.embeddings!.map((v) => cosineSimilarity(questionModel!.vector, v)),
      ),
    }))
    .filter((c) => c.score >= RELEVANCE_THRESHOLD);

  // Stichworttreffer über ALLE übrigen Dokumente – also auch über solche
  // ohne Embedding oder mit veraltetem Modell.
  //
  // Vorher fielen genau diese Dokumente stillschweigend heraus, sobald auch
  // nur ein einziges Dokument ein Embedding hatte. Am 07.08.2026 führte das
  // dazu, dass ein abgelegter AVV auf die Frage "Habe ich mit ZAP einen AVV?"
  // gar nicht erst betrachtet wurde.
  const bereitsGefunden = new Set(perVektor.map((c) => c.entry.document.id));
  const terms = question.toLowerCase().split(/\W+/u).filter((term) => term.length > 3);
  const perStichwort = documents
    .filter((entry) => !bereitsGefunden.has(entry.document.id))
    .map((entry) => ({
      entry,
      treffer: terms.filter((term) => entry.text.toLowerCase().includes(term)).length,
    }))
    .filter((c) => c.treffer > 0)
    // Auf die Skala der Ähnlichkeitswerte bringen, damit beide Wege in einer
    // gemeinsamen Rangfolge stehen. Bewusst knapp über der Schwelle: ein
    // Vektortreffer ist aussagekräftiger als ein bloßes Stichwort.
    .map((c) => ({ entry: c.entry, score: RELEVANCE_THRESHOLD + 0.01 * c.treffer }));

  const relevant = [...perVektor, ...perStichwort]
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_DOCUMENTS_PER_QUERY)
    .map((c) => c.entry);

  if (relevant.length === 0) {
    return {
      answer: 'Dazu liegen mir keine passenden Unterlagen vor.',
      sources: [],
      computed: null,
    };
  }

  const computed = computeFromFields(relevant);

  const result = await api.post<{
    answer: string;
    sources: Array<{ documentId: string; filename: string }>;
  }>('/api/ai/answer', {
    question,
    computed,
    excerpts: relevant.map((entry) => ({
      documentId: entry.document.id,
      filename: entry.document.filename,
      createdAt: entry.document.meta.created_at,
      categoryLabel: entry.document.meta.category_label,
      text: entry.text,
      structuredFields: entry.fields,
    })),
  });

  return { answer: result.answer, sources: result.sources, computed };
}
